import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarIcon, Users, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { booking } from '../lib/apiconfig';
import { toast } from 'sonner';
import { roomTypes } from '@/lib/config';
import { postApi } from '@/lib/apiHelper';

const BookingForm = ({ prefilledCheckIn, prefilledCheckOut, prefilledGuests, prefilledRoomType }) => {
  const [loading, setLoading] = useState(false);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    roomType: '',
    guests: '2',
    specialRequests: ''
  });

  // Update form when prefilled values change
  useEffect(() => {
    if (prefilledCheckIn) {
      setCheckIn(prefilledCheckIn);
    }
    if (prefilledCheckOut) {
      setCheckOut(prefilledCheckOut);
    }
    if (prefilledGuests) {
      setFormData(prev => ({ ...prev, guests: prefilledGuests }));
    }
    if (prefilledRoomType) {
      setFormData(prev => ({ ...prev, roomType: prefilledRoomType }));
    }
  }, [prefilledCheckIn, prefilledCheckOut, prefilledGuests, prefilledRoomType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates.');
      return;
    }

    if (!formData.roomType) {
      toast.error('Please select a room type.');
      return;
    }

    setLoading(true);

    const bookingData = {
      ...formData,
      checkIn: format(checkIn, 'yyyy-MM-dd'),
      checkOut: format(checkOut, 'yyyy-MM-dd')
    };

    postApi(booking, bookingData).then((result) => {
      if (result.data.success) {
        toast.success(
          `Booking Submitted! Your booking request (${result.data.bookingId}) has been received. We'll contact you shortly.`
        );

        setFormData({
          name: '',
          email: '',
          phone: '',
          roomType: '',
          guests: '2',
          specialRequests: ''
        });
        setCheckIn(null);
        setCheckOut(null);
      } else {
        toast.error(result.message || 'Failed to submit booking. Please try again.');
      }
    })
      .catch(() => {
        toast.error('Failed to submit booking. Please try again.');
      })
      .finally(() => {
        setLoading(false);
      });
  };


  return (
    <section id="booking" className="booking-section">
      <div className="section-container">
        <div className="section-header">
          <h2 className="section-title">Reserve Your Stay</h2>
          <p className="section-subtitle">
            Begin your journey to paradise. Book your unforgettable experience today.
          </p>
        </div>

        <div className="booking-container">
          <form onSubmit={handleSubmit} className="booking-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="name">Full Name *</Label>
                <Input id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="John Doe" required />
              </div>

              <div className="form-group">
                <Label htmlFor="email">Email Address *</Label>
                <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="john@example.com" required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="phone">Phone Number *</Label>
                <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="+1 234 567 8900" required />
              </div>

              <div className="form-group">
                <Label htmlFor="guests">Number of Guests *</Label>
                <Select value={formData.guests} onValueChange={(value) => setFormData(prev => ({ ...prev, guests: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select guests" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5, 6].map((num) => (
                      <SelectItem key={num} value={num.toString()}>
                        {num} {num === 1 ? 'Guest' : 'Guests'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Label>Check-In Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="date-picker-button">
                      <CalendarIcon size={18} />
                      {checkIn ? format(checkIn, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={checkIn}
                      onSelect={setCheckIn}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="form-group">
                <Label>Check-Out Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="date-picker-button">
                      <CalendarIcon size={18} />
                      {checkOut ? format(checkOut, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={checkOut}
                      onSelect={setCheckOut}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return true;
                        if (checkIn) {
                          const nextDay = new Date(checkIn);
                          nextDay.setDate(nextDay.getDate() + 1);
                          nextDay.setHours(0, 0, 0, 0);
                          return date < nextDay;
                        }
                        return false;
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="form-group mb-3">
              <Label htmlFor="roomType">Room Type *</Label>
              <Select
                value={formData.roomType}
                onValueChange={(value) => setFormData(prev => ({ ...prev, roomType: value }))}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select room type" />
                </SelectTrigger>
                <SelectContent>
                  {roomTypes.map((room) => (
                    <SelectItem key={room.id} value={room.name}>
                      {room.name} - ${room.price}/night
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="specialRequests">Special Requests</Label>
              <Textarea
                id="specialRequests"
                name="specialRequests"
                value={formData.specialRequests}
                onChange={handleInputChange}
                placeholder="Any special requests or requirements..."
                rows={4}
              />
            </div>

            <Button type="submit" className="booking-submit-button" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Processing...
                </>
              ) : (
                'Submit Booking Request'
              )}
            </Button>
          </form>

          <div className="booking-info">
            <h3 className="booking-info-title">Why Book Direct?</h3>
            <ul className="booking-benefits">
              <li>Best Rate Guarantee</li>
              <li>Flexible Cancellation</li>
              <li>Complimentary Breakfast</li>
              <li>Room Upgrade (Subject to availability)</li>
              <li>Late Check-out Options</li>
              <li>Exclusive Member Perks</li>
            </ul>

            <div className="booking-contact">
              <h4>Need Assistance?</h4>
              <p>Call us: <a href="tel:+917358481281">+91 73584 81281</a></p>
              <p>Email: <a href="mailto:luxtansaresorts@gmail.com">luxtansaresorts@gmail.com</a></p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingForm;
